import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RotateCcw, ArrowLeft } from "lucide-react";

// CalcErrorBoundary — guards a single calculator render path so a thrown
// runtime error inside one calculator does not blank the entire application.
//
// Operating doctrine:
//   1. Catch render-phase exceptions only (React's contract).
//   2. Log a structured diagnostic envelope to console (and to localStorage
//      for offline retrieval — see `_persist`).
//   3. Render a calm, operator-friendly recovery card with three options:
//        a. Reload this calculator       (resets error state)
//        b. Back to Categories           (escape hatch)
//        c. View diagnostic details      (collapsed unless requested)
//   4. Adjacent calculators stay alive because the boundary only owns this
//      sub-tree.
//
// Props:
//   - calcLabel:   string shown in the recovery card ("Driller's Method")
//   - onReset:     optional callback invoked when the user clicks Reload
//   - children:    the protected sub-tree (usually <CalculatorRunner />)
//
// data-testid hooks: `calc-error-boundary`, `calc-error-reload`,
// `calc-error-back`, `calc-error-details-toggle`, `calc-error-details`.

const DIAG_KEY = "rsc.calc.errors.v1";
const DIAG_MAX = 20;

function _persist(envelope) {
  try {
    const list = JSON.parse(localStorage.getItem(DIAG_KEY) || "[]");
    list.unshift(envelope);
    while (list.length > DIAG_MAX) list.pop();
    localStorage.setItem(DIAG_KEY, JSON.stringify(list));
  } catch {
    /* localStorage may be sealed in private browsing — ignore */
  }
}

export default class CalcErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    const envelope = {
      at:        new Date().toISOString(),
      calcLabel: this.props.calcLabel || "Unknown calculator",
      message:   String(error?.message || error),
      stack:     String(error?.stack || ""),
      component: String(info?.componentStack || ""),
      ua:        navigator.userAgent,
    };
    // eslint-disable-next-line no-console
    console.error("[RigSentinel] Calculator render error:", envelope);
    _persist(envelope);
    this.setState({ info });
  }

  handleReload = () => {
    this.setState({ error: null, info: null, showDetails: false });
    if (typeof this.props.onReset === "function") {
      try { this.props.onReset(); } catch { /* best effort */ }
    }
  };

  render() {
    const { error, info, showDetails } = this.state;
    if (!error) return this.props.children;

    return (
      <div data-testid="calc-error-boundary"
           className="max-w-2xl mx-auto px-4 py-8">
        <div className="card-rs p-5"
             style={{ borderColor: "rgba(252,165,165,0.5)", background: "rgba(252,165,165,0.06)" }}>
          <div className="flex items-start gap-3">
            <div className="shrink-0 mt-1">
              <AlertTriangle className="w-6 h-6 text-[#FCA5A5]" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#FCA5A5]">
                Calculator error
              </div>
              <h2 className="text-[18px] font-semibold text-[var(--rs-text-1)] mt-0.5">
                Something went wrong inside this calculator.
              </h2>
              <p className="text-[12px] text-[var(--rs-text-2)] mt-2 leading-snug">
                <strong>{this.props.calcLabel || "This calculator"}</strong> hit
                an unexpected runtime error. The rest of RigSentinel is
                unaffected — other calculators, your saved profiles, and any
                in-progress kill sheets are intact.
              </p>
              <p className="text-[11px] text-[var(--rs-text-3)] mt-2 leading-snug">
                If this is reproducible during operations, please capture the
                input values and contact support — a diagnostic log has been
                written to local storage.
              </p>

              <div className="flex flex-wrap items-center gap-2 mt-4">
                <button data-testid="calc-error-reload" type="button"
                        onClick={this.handleReload}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-medium"
                        style={{ background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 40 }}>
                  <RotateCcw className="w-4 h-4" /> Reload this calculator
                </button>
                <Link data-testid="calc-error-back" to="/categories"
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px]"
                      style={{ background: "transparent", color: "var(--rs-text-2)", border: "1px solid var(--rs-border)", minHeight: 40 }}>
                  <ArrowLeft className="w-4 h-4" /> Back to Categories
                </Link>
                <button data-testid="calc-error-details-toggle" type="button"
                        onClick={() => this.setState({ showDetails: !showDetails })}
                        className="text-[11px] underline text-[var(--rs-text-3)] hover:text-[var(--rs-text-1)] ml-auto">
                  {showDetails ? "Hide details" : "Show diagnostic details"}
                </button>
              </div>

              {showDetails && (
                <pre data-testid="calc-error-details"
                     className="mt-4 p-3 text-[10.5px] font-mono whitespace-pre-wrap overflow-auto rounded-md"
                     style={{
                       background: "rgba(10,11,14,0.85)",
                       border: "1px solid var(--rs-border)",
                       color: "var(--rs-text-2)",
                       maxHeight: 320,
                     }}>
                  {String(error?.message || error)}{"\n\n"}
                  {String(error?.stack || "")}{"\n\n"}
                  {String(info?.componentStack || "")}
                </pre>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
